import FullCalendar from '@fullcalendar/react';
import { Typography } from '@mui/material';
import {
  ButtonDateRangePicker,
  colors,
  formatDateTimeShortWithLocale,
  FeaturesEnum,
} from 'remote/global';
import {
  useUIAdapterLocale,
  useUISchedulerDateRange,
  useUISchedulerHoveredEvent,
} from 'api/queries/ui';
import { useTranslation } from 'react-i18next';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { getTimeZoneConvertedString } from 'utils/helpers';
import { EventHoveringArg } from '@fullcalendar/core';
import { Box } from '@mui/system';
import FerryHollow from 'assets/FerryHollow';
import { DateRange } from '@mui/x-date-pickers-pro';

function EventHoverSlot({ args }: { args?: EventHoveringArg }) {
  const { i18n } = useTranslation();
  if (args == null) return null;

  const { event } = args ?? {};
  const { bookingNumber, clientStartTime, clientEndTime, clientStartAddress, clientEndAddress } =
    event.extendedProps;
  const includesFerryTrip = event.extendedProps?.features.includes(FeaturesEnum.FERRY);

  return (
    <Box
      sx={{
        display: ['none', null, 'flex'],
        flexDirection: 'column',
        gap: 1,
        margin: '0 auto',
        top: 0,
        width: 350,
        left: '50%',
        transform: 'translateX(-50%)',
        position: 'absolute',
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '20px',
          color: colors.uiBlue600,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ marginRight: '10px' }}># {bookingNumber}</span>
        {includesFerryTrip && <FerryHollow />}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: '18.2px',
          fontWeight: 400,
          color: colors.uiBlue600,
        }}
      >
        {formatDateTimeShortWithLocale(clientStartTime, i18n.language)} - {clientStartAddress}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: '18.2px',
          fontWeight: 400,
          color: colors.uiBlue600,
        }}
      >
        {formatDateTimeShortWithLocale(clientEndTime, i18n.language)} - {clientEndAddress}
      </Typography>
    </Box>
  );
}

type DatePickerProps = {
  calendarRef: React.RefObject<FullCalendar>;
};

function SchedulerViewPageHeaderDatePicker({ calendarRef }: DatePickerProps) {
  const { schedulerDateRange, setSchedulerDateRange } = useUISchedulerDateRange();
  const { adapterLocale } = useUIAdapterLocale();

  return (
    <ButtonDateRangePicker
      dateAdapter={AdapterDayjs}
      adapterLocale={adapterLocale ?? 'en'}
      setDateValue={(value) => setSchedulerDateRange(value as DateRange<Date>)}
      dateValue={schedulerDateRange as DateRange<Date>}
      onClose={async () => {
        const [from, to] = schedulerDateRange;
        const calendarApi = calendarRef?.current?.getApi();
        if (!calendarApi) return;

        if (from != null && to != null) {
          calendarApi.changeView('pastAndFutureView', {
            start: new Date(getTimeZoneConvertedString(from as Date)),
            end: new Date(getTimeZoneConvertedString(to as Date)),
          });
        }
      }}
    />
  );
}

type Props = {
  calendarRef: React.RefObject<FullCalendar>;
};

function SchedulerPageHeader({ calendarRef }: Props) {
  const { schedulerHoveredEvent } = useUISchedulerHoveredEvent();

  return (
    <>
      {schedulerHoveredEvent != null && <EventHoverSlot args={schedulerHoveredEvent} />}
      <SchedulerViewPageHeaderDatePicker calendarRef={calendarRef} />
    </>
  );
}

export default SchedulerPageHeader;
